// src/pages/EventActions.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import { ModalConfirm } from '../components/Modal';
import EventActionBindingModal from '../components/EventActionBindingModal';
import { usePagination } from '../hooks/usePagination';
import { useEventTypeSchemas } from '../hooks/useEventTypeSchemas';
import { formatDate, formatRelativeDate } from '../utils/formatters';
import { getPriorityColor } from '../constants/colors';
import {
    BoltIcon,
    ChartBarIcon,
    ListBulletIcon,
    ClockIcon,
    ArrowPathIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    FunnelIcon,
    PlayIcon,
    PauseIcon,
} from '@heroicons/react/24/outline';

const PRIORITY_LABELS = { 0: 'LOW', 1: 'NORMAL', 2: 'HIGH' };

const LOG_STATUS_COLORS = {
    created: 'text-green-700 bg-green-100',
    failed: 'text-red-700 bg-red-100',
    skipped: 'text-yellow-700 bg-yellow-100',
};

const EventActions = () => {
    const { executeQuery, executeMutation, queries, mutations, loading } = useGraphQL();
    const { eventTypesByCategory, categories: eventCategories } = useEventTypeSchemas();

    // Tab state
    const [activeTab, setActiveTab] = useState('bindings');

    // Stats
    const [stats, setStats] = useState(null);

    // Bindings state
    const [bindings, setBindings] = useState([]);
    const [bindingsFilter, setBindingsFilter] = useState({ enabled: null, eventType: '' });
    const bindingsPagination = usePagination({ initialPageSize: 25 });

    // Logs state
    const [logs, setLogs] = useState([]);
    const [logsFilter, setLogsFilter] = useState({ eventType: '', status: '', bindingId: '' });
    const logsPagination = usePagination({ initialPageSize: 25 });

    // Modals
    const [editingBinding, setEditingBinding] = useState(null);
    const [showBindingModal, setShowBindingModal] = useState(false);
    const [deletingBinding, setDeletingBinding] = useState(null);
    const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
    const [cleanupDays, setCleanupDays] = useState(30);

    // Feedback
    const [error, setError] = useState(null);
    const [actionResult, setActionResult] = useState(null);

    // Data loading
    const loadStats = async () => {
        try {
            const data = await executeQuery(queries.getEventActionStats());
            setStats(data.adminEventActionStats);
        } catch (err) {
            console.error('Failed to load event action stats:', err);
            setError(err.message);
        }
    };

    const loadBindings = async () => {
        setError(null);
        try {
            const variables = {
                limit: bindingsPagination.pageSize,
                offset: bindingsPagination.offset,
            };
            if (bindingsFilter.enabled !== null) variables.enabled = bindingsFilter.enabled;
            if (bindingsFilter.eventType) variables.eventType = bindingsFilter.eventType;
            const data = await executeQuery(queries.getEventActionBindings(), variables);
            setBindings(data.adminEventActionBindings.items);
            bindingsPagination.setTotalItems(data.adminEventActionBindings.totalCount);
        } catch (err) {
            console.error('Failed to load bindings:', err);
            setError(err.message);
        }
    };

    const loadLogs = async () => {
        setError(null);
        try {
            const variables = {
                limit: logsPagination.pageSize,
                offset: logsPagination.offset,
            };
            if (logsFilter.eventType) variables.eventType = logsFilter.eventType;
            if (logsFilter.status) variables.status = logsFilter.status;
            if (logsFilter.bindingId) variables.bindingId = logsFilter.bindingId;
            const data = await executeQuery(queries.getEventActionLogs(), variables);
            setLogs(data.adminEventActionLogs.items);
            logsPagination.setTotalItems(data.adminEventActionLogs.totalCount);
        } catch (err) {
            console.error('Failed to load logs:', err);
            setError(err.message);
        }
    };

    // Load initial data
    useEffect(() => {
        loadStats();
    }, []);

    useEffect(() => {
        if (activeTab === 'bindings') loadBindings();
    }, [activeTab, bindingsPagination.currentPage, bindingsFilter]);

    useEffect(() => {
        if (activeTab === 'logs') loadLogs();
    }, [activeTab, logsPagination.currentPage, logsFilter]);

    // Actions
    const handleToggleBinding = async (binding) => {
        setError(null);
        try {
            const mutation = binding.enabled
                ? mutations.adminDisableEventActionBinding
                : mutations.adminEnableEventActionBinding;
            const result = await executeMutation(mutation, { id: binding.id });
            const key = binding.enabled ? 'adminDisableEventActionBinding' : 'adminEnableEventActionBinding';
            if (result[key]?.success) {
                loadBindings();
                loadStats();
            } else {
                setError(result[key]?.error || 'Failed to toggle binding');
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteBinding = async () => {
        if (!deletingBinding) return;
        setError(null);
        try {
            const result = await executeMutation(mutations.adminDeleteEventActionBinding, { id: deletingBinding.id });
            if (result.adminDeleteEventActionBinding?.success) {
                setActionResult({ type: 'success', message: 'Binding deleted successfully' });
                loadBindings();
                loadStats();
            } else {
                setError(result.adminDeleteEventActionBinding?.error || 'Failed to delete binding');
            }
        } catch (err) {
            setError(err.message);
        }
        setDeletingBinding(null);
    };

    const handleCleanupLogs = async () => {
        setError(null);
        try {
            const result = await executeMutation(mutations.adminCleanupEventActionLogs, { olderThanDays: cleanupDays });
            if (result.adminCleanupEventActionLogs?.success) {
                setActionResult({
                    type: 'success',
                    message: `Cleaned up ${result.adminCleanupEventActionLogs.deletedCount} log entries older than ${cleanupDays} days`,
                });
                loadLogs();
                loadStats();
            } else {
                setError(result.adminCleanupEventActionLogs?.message || 'Cleanup failed');
            }
        } catch (err) {
            setError(err.message);
        }
        setShowCleanupConfirm(false);
    };

    const handleBindingSaved = () => {
        loadBindings();
        loadStats();
        setActionResult({ type: 'success', message: editingBinding ? 'Binding updated' : 'Binding created' });
    };

    const openCreateModal = () => {
        setEditingBinding(null);
        setShowBindingModal(true);
    };

    const openEditModal = (binding) => {
        setEditingBinding(binding);
        setShowBindingModal(true);
    };

    // Cross-tab navigation: view logs filtered to a specific binding
    const viewLogsForBinding = (bindingId) => {
        setLogsFilter({ eventType: '', status: '', bindingId });
        logsPagination.reset();
        setActiveTab('logs');
    };

    const getPriorityBadgeClass = (priority) => {
        const label = PRIORITY_LABELS[priority] || 'NORMAL';
        return getPriorityColor(label);
    };

    const tabs = [
        { id: 'bindings', label: 'Bindings', icon: ListBulletIcon },
        { id: 'logs', label: 'Dispatch Logs', icon: ClockIcon },
        { id: 'stats', label: 'Dashboard', icon: ChartBarIcon },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <BoltIcon className="h-8 w-8 text-primary" />
                    <h1 className="text-2xl font-bold text-slate-900">Event Actions</h1>
                </div>
                <div className="flex items-center space-x-3">
                    {activeTab === 'bindings' && (
                        <button
                            onClick={openCreateModal}
                            className="px-4 py-2 rounded-lg bg-primary text-on-accent font-mono text-xs font-bold uppercase hover:brightness-110 transition-all duration-150 flex items-center space-x-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            <span>New Binding</span>
                        </button>
                    )}
                    <button
                        onClick={() => {
                            loadStats();
                            if (activeTab === 'bindings') loadBindings();
                            if (activeTab === 'logs') loadLogs();
                        }}
                        className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 flex items-center space-x-2"
                        disabled={loading}
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        <span className="font-mono text-xs font-bold uppercase">Refresh</span>
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 text-sm font-mono">Error: {error}</p>
                </div>
            )}

            {/* Action Result */}
            {actionResult && (
                <div className={`${actionResult.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            {actionResult.type === 'success' ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                            ) : (
                                <XCircleIcon className="h-5 w-5 text-red-600" />
                            )}
                            <p className={`text-sm ${actionResult.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                                {actionResult.message}
                            </p>
                        </div>
                        <button onClick={() => setActionResult(null)} className="text-sm text-slate-500 hover:text-slate-700">
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Quick Stats Bar */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="surface-card p-3">
                        <p className="text-xs font-mono text-slate-400 uppercase">Total Bindings</p>
                        <p className="text-xl font-bold text-slate-900">{stats.totalBindings}</p>
                    </div>
                    <div className="surface-card p-3">
                        <p className="text-xs font-mono text-slate-400 uppercase">Enabled</p>
                        <p className="text-xl font-bold text-green-600">{stats.enabledBindings}</p>
                    </div>
                    <div className="surface-card p-3">
                        <p className="text-xs font-mono text-slate-400 uppercase">Disabled</p>
                        <p className="text-xl font-bold text-slate-400">{stats.disabledBindings}</p>
                    </div>
                    <div className="surface-card p-3">
                        <p className="text-xs font-mono text-slate-400 uppercase">Total Dispatched</p>
                        <p className="text-xl font-bold text-slate-900">{stats.totalEventsDispatched?.toLocaleString()}</p>
                    </div>
                    <div className="surface-card p-3">
                        <p className="text-xs font-mono text-slate-400 uppercase">Last 24h</p>
                        <p className="text-xl font-bold text-primary">{stats.recentEventsDispatched?.toLocaleString()}</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-700">
                <nav className="flex space-x-8">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === tab.id
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                        >
                            <div className="flex items-center space-x-2">
                                <tab.icon className="h-5 w-5" />
                                <span>{tab.label}</span>
                            </div>
                        </button>
                    ))}
                </nav>
            </div>

            {/* ===================== BINDINGS TAB ===================== */}
            {activeTab === 'bindings' && (
                <div className="space-y-4">
                    {/* Filters */}
                    <div className="flex items-center gap-3">
                        <FunnelIcon className="h-4 w-4 text-slate-400" />
                        <select
                            value={bindingsFilter.enabled === null ? '' : String(bindingsFilter.enabled)}
                            onChange={(e) => {
                                const val = e.target.value;
                                setBindingsFilter(f => ({
                                    ...f,
                                    enabled: val === '' ? null : val === 'true',
                                }));
                                bindingsPagination.reset();
                            }}
                            className="text-sm font-mono"
                        >
                            <option value="">All statuses</option>
                            <option value="true">Enabled</option>
                            <option value="false">Disabled</option>
                        </select>
                        <select
                            value={bindingsFilter.eventType}
                            onChange={(e) => {
                                setBindingsFilter(f => ({ ...f, eventType: e.target.value }));
                                bindingsPagination.reset();
                            }}
                            className="text-sm font-mono"
                        >
                            <option value="">All event types</option>
                            {eventCategories.map(cat => {
                                const catEvents = eventTypesByCategory[cat.id] || [];
                                if (catEvents.length === 0) return null;
                                return (
                                    <optgroup key={cat.id} label={cat.label}>
                                        {catEvents.map(et => (
                                            <option key={et.eventType} value={et.eventType}>{et.eventType}</option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>
                        <span className="text-xs text-slate-400 font-mono ml-auto">{bindingsPagination.totalItems} total</span>
                    </div>

                    {/* Bindings Table */}
                    <div className="surface-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Event Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Job Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Description</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Priority</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Condition</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Updated</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {bindings.map(b => (
                                        <tr key={b.id} className="hover:bg-slate-50">
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => handleToggleBinding(b)}
                                                    title={b.enabled ? 'Click to disable' : 'Click to enable'}
                                                    className={`px-2 py-1 rounded text-xs font-mono font-bold uppercase transition-all duration-150 ${
 b.enabled
 ? 'text-green-700 bg-green-100 hover:bg-green-200'
 : 'text-slate-500 bg-slate-100 hover:bg-slate-200'
 }`}
                                                >
                                                    {b.enabled ? 'ON' : 'OFF'}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-900 font-mono">
                                                <div className="flex items-center gap-2">
                                                    {b.eventType}
                                                    {b.managedBy === 'yaml' && (
                                                        <span
                                                            className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-mono text-[10px] font-bold"
                                                            title={`Owned by workflows/_schedule.yaml (${b.managedName || 'unnamed'}) — edits/deletes are reverted on reconcile; toggling is temporary`}
                                                        >
                                                            YAML
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-600 font-mono">{b.jobType}</td>
                                            <td className="px-4 py-3 text-sm text-slate-500 max-w-xs truncate">{b.description || '-'}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-mono ${getPriorityBadgeClass(b.jobPriority)}`}>
                                                    {PRIORITY_LABELS[b.jobPriority] || b.jobPriority}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-400 font-mono max-w-[12rem] truncate">{b.condition || '-'}</td>
                                            <td className="px-4 py-3 text-xs text-slate-400">{formatRelativeDate(b.updatedAt)}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center space-x-1">
                                                    {b.managedBy !== 'yaml' && (
                                                        <button
                                                            onClick={() => openEditModal(b)}
                                                            className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                                            title="Edit"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleToggleBinding(b)}
                                                        className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                                        title={(b.enabled ? 'Disable' : 'Enable') + (b.managedBy === 'yaml' ? ' (temporary — next reconcile restores the YAML state)' : '')}
                                                    >
                                                        {b.enabled ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => viewLogsForBinding(b.id)}
                                                        className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                                        title="View logs"
                                                    >
                                                        <ClockIcon className="h-4 w-4" />
                                                    </button>
                                                    {b.managedBy !== 'yaml' && (
                                                        <button
                                                            onClick={() => setDeletingBinding(b)}
                                                            className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                                                            title="Delete"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {bindings.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                                                <BoltIcon className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                                                <p className="font-mono text-sm">No bindings found</p>
                                                <button onClick={openCreateModal} className="mt-3 text-primary hover:underline text-sm font-mono">
                                                    Create your first binding
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {bindingsPagination.totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                                <p className="text-xs text-slate-400 font-mono">
                                    Page {bindingsPagination.currentPage} of {bindingsPagination.totalPages}
                                </p>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={bindingsPagination.prevPage}
                                        disabled={!bindingsPagination.hasPrevPage}
                                        className="px-3 py-1 rounded-lg text-xs font-mono bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={bindingsPagination.nextPage}
                                        disabled={!bindingsPagination.hasNextPage}
                                        className="px-3 py-1 rounded-lg text-xs font-mono bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===================== LOGS TAB ===================== */}
            {activeTab === 'logs' && (
                <div className="space-y-4">
                    {/* Filters */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <FunnelIcon className="h-4 w-4 text-slate-400" />
                        <select
                            value={logsFilter.status}
                            onChange={(e) => {
                                setLogsFilter(f => ({ ...f, status: e.target.value }));
                                logsPagination.reset();
                            }}
                            className="text-sm font-mono"
                        >
                            <option value="">All statuses</option>
                            <option value="created">Created</option>
                            <option value="failed">Failed</option>
                            <option value="skipped">Skipped</option>
                        </select>
                        <select
                            value={logsFilter.eventType}
                            onChange={(e) => {
                                setLogsFilter(f => ({ ...f, eventType: e.target.value }));
                                logsPagination.reset();
                            }}
                            className="text-sm font-mono"
                        >
                            <option value="">All event types</option>
                            {eventCategories.map(cat => {
                                const catEvents = eventTypesByCategory[cat.id] || [];
                                if (catEvents.length === 0) return null;
                                return (
                                    <optgroup key={cat.id} label={cat.label}>
                                        {catEvents.map(et => (
                                            <option key={et.eventType} value={et.eventType}>{et.eventType}</option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>
                        {logsFilter.bindingId && (
                            <div className="flex items-center gap-1 bg-slate-100 rounded px-2 py-1 text-xs font-mono text-slate-600">
                                <span>binding: {logsFilter.bindingId.slice(0, 8)}...</span>
                                <button
                                    onClick={() => {
                                        setLogsFilter(f => ({ ...f, bindingId: '' }));
                                        logsPagination.reset();
                                    }}
                                    className="ml-1 text-slate-400 hover:text-slate-700"
                                >
                                    &times;
                                </button>
                            </div>
                        )}
                        <div className="ml-auto flex items-center gap-3">
                            <span className="text-xs text-slate-400 font-mono">{logsPagination.totalItems} total</span>
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={cleanupDays}
                                    onChange={(e) => setCleanupDays(Number(e.target.value) || 30)}
                                    className="w-14 text-xs font-mono text-center"
                                    title="Days to keep"
                                />
                                <button
                                    onClick={() => setShowCleanupConfirm(true)}
                                    className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 text-xs font-mono font-bold uppercase flex items-center space-x-1"
                                >
                                    <TrashIcon className="h-3.5 w-3.5" />
                                    <span>Cleanup</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Logs Table */}
                    <div className="surface-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Event Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Job Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Job ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Tender ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Source</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Error</th>
                                        <th className="px-4 py-3 text-left text-xs font-mono font-bold text-slate-500 uppercase">Time</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {logs.map(log => (
                                        <tr key={log.id} className="hover:bg-slate-50">
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold uppercase ${LOG_STATUS_COLORS[log.status] || 'text-slate-500 bg-slate-100'}`}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-900 font-mono">{log.eventType}</td>
                                            <td className="px-4 py-3 text-sm text-slate-600 font-mono">{log.jobType || '-'}</td>
                                            <td className="px-4 py-3 text-xs text-slate-400 font-mono">
                                                {log.jobId ? `${log.jobId.slice(0, 8)}...` : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-400 font-mono">
                                                {log.tenderId ? `${log.tenderId.slice(0, 8)}...` : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-400">{log.eventSource || '-'}</td>
                                            <td className="px-4 py-3 text-xs text-red-600 max-w-xs truncate">
                                                {log.errorMessage || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-400" title={formatDate(log.createdAt)}>
                                                {formatRelativeDate(log.createdAt)}
                                            </td>
                                        </tr>
                                    ))}
                                    {logs.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                                                <ClockIcon className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                                                <p className="font-mono text-sm">No dispatch logs found</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {logsPagination.totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                                <p className="text-xs text-slate-400 font-mono">
                                    Page {logsPagination.currentPage} of {logsPagination.totalPages}
                                </p>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={logsPagination.prevPage}
                                        disabled={!logsPagination.hasPrevPage}
                                        className="px-3 py-1 rounded-lg text-xs font-mono bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={logsPagination.nextPage}
                                        disabled={!logsPagination.hasNextPage}
                                        className="px-3 py-1 rounded-lg text-xs font-mono bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===================== STATS/DASHBOARD TAB ===================== */}
            {activeTab === 'stats' && stats && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Enabled Bindings</p>
                                    <p className="text-3xl font-bold text-green-600">{stats.enabledBindings}</p>
                                    <p className="text-sm text-slate-400">{stats.disabledBindings} disabled</p>
                                </div>
                                <CheckCircleIcon className="h-10 w-10 text-slate-300" />
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Events Dispatched</p>
                                    <p className="text-3xl font-bold text-slate-900">{stats.totalEventsDispatched?.toLocaleString()}</p>
                                    <p className="text-sm text-slate-400">all time</p>
                                </div>
                                <BoltIcon className="h-10 w-10 text-slate-300" />
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Recent Activity (24h)</p>
                                    <p className="text-3xl font-bold text-primary">{stats.recentEventsDispatched?.toLocaleString()}</p>
                                    <p className="text-sm text-slate-400">events dispatched</p>
                                </div>
                                <ClockIcon className="h-10 w-10 text-slate-300" />
                            </div>
                        </div>
                    </div>

                    {/* Event Type Breakdown */}
                    {stats.eventTypeBreakdown && stats.eventTypeBreakdown.length > 0 && (
                        <div className="surface-card p-6">
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">Events by Type</h3>
                            <div className="space-y-2">
                                {stats.eventTypeBreakdown
                                    .sort((a, b) => b.count - a.count)
                                    .map(entry => {
                                        const maxCount = stats.eventTypeBreakdown.reduce((max, e) => Math.max(max, e.count), 0);
                                        const pct = maxCount > 0 ? (entry.count / maxCount) * 100 : 0;
                                        return (
                                            <div key={entry.eventType} className="flex items-center gap-3">
                                                <span className="text-sm font-mono text-slate-600 w-64 truncate">{entry.eventType}</span>
                                                <div className="flex-1 bg-slate-100 rounded-full h-2">
                                                    <div
                                                        className="bg-primary rounded-full h-2 transition-all"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <span className="text-sm font-mono text-slate-500 w-16 text-right">{entry.count.toLocaleString()}</span>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}

                    {stats.eventTypeBreakdown?.length === 0 && (
                        <div className="surface-card p-12 text-center">
                            <ExclamationTriangleIcon className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                            <p className="text-slate-400 font-mono text-sm">No event dispatch data yet</p>
                        </div>
                    )}
                </div>
            )}

            {/* ===================== MODALS ===================== */}
            <EventActionBindingModal
                isOpen={showBindingModal}
                onClose={() => {
                    setShowBindingModal(false);
                    setEditingBinding(null);
                }}
                onSaved={handleBindingSaved}
                binding={editingBinding}
                executeMutation={executeMutation}
                mutations={mutations}
            />

            <ModalConfirm
                isOpen={!!deletingBinding}
                onClose={() => setDeletingBinding(null)}
                onConfirm={handleDeleteBinding}
                title="Delete Binding"
                message={`Delete binding "${deletingBinding?.description || deletingBinding?.eventType + ' → ' + deletingBinding?.jobType}"? This cannot be undone.`}
                confirmLabel="Delete"
                type="danger"
            />

            <ModalConfirm
                isOpen={showCleanupConfirm}
                onClose={() => setShowCleanupConfirm(false)}
                onConfirm={handleCleanupLogs}
                title="Cleanup Dispatch Logs"
                message={`Delete all dispatch log entries older than ${cleanupDays} days. This cannot be undone.`}
                confirmLabel="Cleanup"
                type="warning"
            />
        </div>
    );
};

export default EventActions;
